import {useRef} from 'react';
import {useTranslations} from 'next-intl';
import Container from '@/container';
import {Grid} from '@/grid';
import {SectionPadding} from '@/section';
import Link from '@/links';
import PictureWrapper from '@/ui/images-wrapper';
import ModalWrapper from '@/ui/modal-wrapper';
import StatsStrip from '@/ui/stats-strip';
import {H2, H3, P, PNoMargin} from '@/typography';
import {withBgNeutral200, withNeutral700} from '@/hocs/withColors';
import {
  withBoldWeight,
  withHeadingEyebrow,
  withSecondaryFontTestimonial
} from '@/hocs/withFontType';
import {VideoModalButton} from '@/content/features/shared';
import customersStyles from '../../customers/testimonials-section/styles.module.scss';
import styles from '../../industry-shared/customer-stories-section.module.scss';
import localStyles from './customer-stories-section.module.scss';

const Neutral200Section = withBgNeutral200(SectionPadding);
const QuoteP = withSecondaryFontTestimonial(PNoMargin);
const NameP = withBoldWeight(PNoMargin);
const EyebrowNeutral700 = withNeutral700(withHeadingEyebrow(P as never));
const imgPath = '/static/images/industry-real-estate-and-property-management/';

type StoryStatLabel = {
  label: string;
};

// Stat values are kept inline (not translatable copy) and zipped with the labels from messages.
const STAT_VALUES = ['180%', '100'];

type Testimonial = {
  quote: string;
  nameTitle: string;
  company: string;
  imageUrl: string;
  imageAlt: string;
};

/** Grid forwards `children` via createProps; PropTypes-only export omits it from types. */
const FluidGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

const CustomerStoriesSection = () => {
  const t = useTranslations('industryRealEstate.customerStories');
  const videoButtonRef = useRef<HTMLButtonElement | null>(null);
  const statLabels = t.raw('featured.stats') as StoryStatLabel[];
  const stats = statLabels.map((stat, index) => ({
    value: STAT_VALUES[index] ?? '',
    label: stat.label
  }));
  const testimonial = t.raw('testimonial2') as Testimonial;

  return (
    <Neutral200Section>
      <Container>
        <FluidGrid fluid>
          <H2 className={styles.title}>{t('title')}</H2>

          <div className={localStyles['stories-grid']}>
            <article className={`${styles['featured-card']} ${localStyles.featured}`}>
              <div className={`${styles.media} ${styles['media--featured']}`}>
                <PictureWrapper
                  imgPath={imgPath}
                  responsiveImages={{
                    desktopImageUrl: 'peakmade',
                    mobileImageUrl: 'peakmade-mob'
                  }}
                  imageTypeFallback="jpg"
                  altImage={t('featured.imageAlt')}
                  className={localStyles['featured-image']}
                  loading="lazy"
                />
              </div>

              <div className={styles['featured-content']}>
                <StatsStrip items={stats} columns={2} />
                <EyebrowNeutral700 className={styles.eyebrow}>
                  {t('featured.eyebrow')}
                </EyebrowNeutral700>
                <H3 className={styles['featured-title']}>{t('featured.title')}</H3>
                <P className={styles['featured-copy']}>{t('featured.description')}</P>
                <Link
                  href={t('featured.linkUrl')}
                  variant="cta-default-xs"
                  target={t('featured.linkTarget')}>
                  {t('featured.linkLabel')}
                </Link>
              </div>
            </article>

            <div className={localStyles.video}>
              <article className={styles['testimonial-card']}>
                <ModalWrapper variant="modal-btn-wrapper" className={localStyles['video-modal']}>
                  <div className={customersStyles['video-overlay']}>
                    <div className={customersStyles['video-overlay__logo']}>
                      <img
                        src={`${imgPath}${t('video.logoUrl')}.png`}
                        alt={t('video.logoAlt')}
                        width={244}
                        height={120}
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <p className={`heading-3 ${customersStyles['video-caption']}`}>
                    {t('video.caption')}
                  </p>
                  <PictureWrapper
                    imgPath={imgPath}
                    imageUrl={t('video.imageUrl')}
                    imageTypeFallback="jpg"
                    altImage={t('video.imageAlt')}
                    className={localStyles['video-thumb']}
                    loading="lazy"
                    width={584}
                    height={336}
                    onClick={() => videoButtonRef.current?.click()}
                  />
                  <VideoModalButton
                    ref={videoButtonRef}
                    openName="realEstateCustomerVideo"
                    videoId={t('video.videoId')}
                    variant="image-green"
                    customAttributes={{
                      'data-gtm': 'real-estate-customer-stories-video'
                    }}
                    transcript={t('video.transcript')}
                  />
                </ModalWrapper>
              </article>
            </div>

            <div className={localStyles.testimonial}>
              <article className={styles['testimonial-card']}>
                <div className={styles.media}>
                  <PictureWrapper
                    imgPath={imgPath}
                    imageUrl={testimonial.imageUrl}
                    imageTypeFallback="jpg"
                    altImage={testimonial.imageAlt}
                    className={styles['testimonial-image']}
                    loading="lazy"
                  />
                </div>

                <div className={styles['testimonial-content']}>
                  <QuoteP className={styles.quote}>{testimonial.quote}</QuoteP>
                  <NameP className={styles.name}>{testimonial.nameTitle}</NameP>
                  <PNoMargin className={styles.company}>{testimonial.company}</PNoMargin>
                </div>
              </article>
            </div>
          </div>
        </FluidGrid>
      </Container>
    </Neutral200Section>
  );
};

export default CustomerStoriesSection;
